import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, makeStyles } from '@material-ui/core';
import ScreenTextContext from '../../contexts/ScreenTextContext';
import { BackNextButtons } from './BackNextButtons';
import { api } from '../../utils/api';
import { CATEGORIES_ORDER, MODES } from '../../utils/constants';

const useStyles = makeStyles( (theme) => ({
  checkGroup: {
    flexDirection: 'row',
    width: '100%',
    maxWidth: '110rem',
    margin: '1rem auto -3rem',
    justifyContent: 'center',
  },
  checkLabelWrapper: {
    flex: '0 1 20%',
    padding: '1rem 2rem',
  },
  checkLabel: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: '#fff',
    textAlign: 'center',
    '&:hover': {
      '& .active-icon': {
        opacity: 1,
        // filter: 'brightness(110%)',
      }
    },
  },
  figure: {
    fontFamily: 'museo-sans',
  },
  figureActive: {
    '& .active-icon': {
      opacity: 1,
    }
  },
  imgWrapper: {
    position: 'relative',
    display: 'inline-block'
  },
  activeIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
  },

}));


export const ChooseCategories = ({changeStep, categories, mode, setCategories, setLecture}) => {

  const classes = useStyles();
  
  const [boxes, setBoxes] = useState(new Set(categories));
  const [focusedBoxes, setFocusedBoxes] = useState(new Set());

  // Load screen text
  const { screenText } = useContext(ScreenTextContext);    

  const categoryCodes = useMemo(() => (mode === MODES.LECTURE ? ["CPP", "HAR", "RAS", "TOX"] : ["CPP","DEC","HAR","HDA","PPE","PCT","RAS","ROR","TOX"]), [mode]);

  // remove categories that don't exist for lecture mode
  useEffect(() => {
    if (mode === MODES.LECTURE) {

      // make a copy
      const newBoxes = new Set(boxes);
      
      // keep track of if we will need to set a new state
      let updateNeeded = false;

      newBoxes.forEach(box => {
        // see if selected checkbox is in lecture categories or not
        if (categoryCodes.indexOf(box) < 0) {
          // found one that isn't in lecture categories, so get rid of it.
          newBoxes.delete(box)
          // and because of this we'll need to update state
          updateNeeded = true;
        }
      })

      if (updateNeeded) {
        setBoxes(newBoxes)
      }
    }
  }, [boxes, categoryCodes, mode])

  const handleChange = ({target:box}) => {
    const newBoxes = new Set(boxes)
    if (box.checked) {
      newBoxes.add(box.name)
    } else {
      newBoxes.delete(box.name)
    }
    setBoxes(newBoxes);
  };

  const handleFocus = useCallback(({target:box}) => {
    const newFocusedBoxes = new Set(focusedBoxes)
    newFocusedBoxes.add(box.name)
    setFocusedBoxes(newFocusedBoxes);
  }, [focusedBoxes])

  const handleBlur = useCallback(({target:box}) => {
    const newFocusedBoxes = new Set(focusedBoxes)
    newFocusedBoxes.delete(box.name)
    setFocusedBoxes(newFocusedBoxes);
  }, [focusedBoxes])


  const noneSelected = useCallback(() => {
    return !boxes.size;
  }, [boxes.size])
  
  // Button handlers
  const handleBack = () => {
    setCategories([...boxes])
    changeStep.backward();
  }

  const handleNext = () => {
    setCategories([...boxes])

    // If lecture mode, get all the lecture tasks; this is done
    // here partly in case in the future they wanted to upload a CSV file
    // for a custom lecture
    if (mode === MODES.LECTURE) {
      let tasks = [];

      // prepare a call for each chosen category, in official order of categories
      const getApiCalls = () => (
        CATEGORIES_ORDER
          .filter(category => boxes.has(category))
          .map(category => api.getLectureTasks(category))
      )

      Promise.all(getApiCalls())
        .then(responses => {
          
          responses.forEach(res => {
            const contents = res.data;
            // merge existing tasks with new ones tacked on the end
            tasks = [...tasks, ...contents.split(',\n')];
          })

          setLecture(tasks);
          
          changeStep.forward();
        })
        .catch(errors => {
          console.log(errors)
        });
    

    } else {
      // empty out lecture, just in case user went through set up 
      // with Lecture mode then changed their mind
      setLecture([]);
      changeStep.forward();
    }

  }


  if (!screenText || !screenText.setUp ) {
    return ''; // Should return error? Loading?
  }
  
  const { categories : categoriesScreenText } = screenText.setUp;
  const { categories : catsText } = screenText.global;


  return (
    <div className="categories page-container">

      <div className="set-up__container set-up__container--outlined set-up__container--min-height flex flex-column" style={{minHeight: 602}}>

        <h1 className="mode__title h2">{categoriesScreenText.title}</h1>

        <fieldset className="w-100 my-auto">

          <legend className="sr-only">{categoriesScreenText.title}</legend>
          
          <FormGroup aria-label="category" name="category" className={classes.checkGroup}>
          
            {categoryCodes
              .sort((a,b) => catsText[a] < catsText[b] ? -1 : (catsText[a] > catsText[b] ? 1 : 0))
              .map((category, i) => {

                const active = boxes.has(category);
                const focused = focusedBoxes.has(category);
                
                return (

                  <div key={i} className={classes.checkLabelWrapper}>
                    
                    <FormControlLabel 
                      className={classes.checkLabel}
                      control={
                        <Checkbox 
                          name={category} 
                          onChange={handleChange} 
                          onBlur={handleBlur}
                          onFocus={handleFocus}
                          checked={active} 
                          className="sr-only"
                        />
                      }
                      label={
                        <figure className={classes.figure + (active ? ' ' + classes.figureActive : '')} style={focused ? { outline: '-webkit-focus-ring-color auto 1px' } : {}}>
                          <div className={classes.imgWrapper}>
                            <img src={`${process.env.PUBLIC_URL}/assets/images/categories/${category}.png`} alt="" className={classes.icon} />
                            <img src={`${process.env.PUBLIC_URL}/assets/images/categories/${category}-active.png`} alt="" className={classes.activeIcon + " active-icon"}/>
                          </div>
                          <figcaption>{catsText[category]}</figcaption>
                        </figure>
                      }
                    />

                  </div>

                )
                
              }

            )}

          </FormGroup>

        </fieldset>
      
      </div>

      <BackNextButtons handleBack={handleBack} handleNext={handleNext} disableNext={noneSelected()} />

    </div>
  )
}